import { useEffect, useMemo } from "react";
import { useQuery, useSubscription } from "@apollo/client/react";
import type { ChatMessage } from "@/stores/chat-store";
import { useChatStore } from "@/stores/chat-store";
import { MessagesBanner } from "./MessagesBanner";
import { ChatWindow, MinimizedChat } from "./ChatWindow";
import {
  ConversationEventsDocument,
  ConversationLifecycleEventsDocument,
  ConversationMessagesDocument,
  ConversationPresenceDocument,
  ConversationReadReceiptsDocument,
  MyConversationsDocument,
  MyselfDocument,
  UnreadCountDocument,
  UnreadCountUpdatesDocument,
} from "@/graphql/generated/operations";
import { logApplicationFailure } from "@/lib/user-error";
import { CODES } from "@/lib/diagnostics/contract";

// Widths (px) used both for layout and for the capacity calculation that
// decides how many windows fit next to the banner before the oldest is
// auto-minimized. Keep in sync with the component className widths.
const BANNER_W = 260;
const WINDOW_W = 336;
const GAP = 12;
const SIDE_PAD = 32;

function capacityFor(innerWidth: number): number {
  const avail = innerWidth - BANNER_W - SIDE_PAD;
  return Math.max(1, Math.floor((avail + GAP) / (WINDOW_W + GAP)));
}

// Single live source per conversation. Mounted by ChatDock for every
// conversation in the store, open or not. It seeds the full message
// history + unread count and owns the one ConversationEvents
// subscription; the open window (ChatView) only renders from the store
// and sends. Keeping this the sole subscriber avoids the duplicate
// subscribe/event-processing an open window would otherwise add.
function ConversationLiveBridge({ conversationId }: { conversationId: string }) {
  const setMessages = useChatStore((s) => s.setMessages);
  const setLastMessage = useChatStore((s) => s.setLastMessage);
  const setUnreadCount = useChatStore((s) => s.setUnreadCount);
  const addMessage = useChatStore((s) => s.addMessage);
  const setTyping = useChatStore((s) => s.setTyping);
  const setPresence = useChatStore((s) => s.setPresence);
  const setReadReceipt = useChatStore((s) => s.setReadReceipt);
  const setLiveError = useChatStore((s) => s.setLiveError);

  const { data: messagesData } = useQuery(ConversationMessagesDocument, {
    variables: { input: { conversationId, take: 100 } },
  });
  const { data: unreadData } = useQuery(UnreadCountDocument, {
    variables: { conversationId },
  });
  const { data: presenceData } = useQuery(ConversationPresenceDocument, {
    variables: { conversationId },
  });
  const { data: receiptsData } = useQuery(ConversationReadReceiptsDocument, {
    variables: { conversationId },
  });

  const seed = useMemo(() => {
    const raw = messagesData?.conversationMessages;
    if (!raw) return undefined;
    return raw.map((m) => ({
      id: m.id,
      conversationId: m.conversationId,
      senderId: m.senderId,
      content: m.content,
      sentAt: m.sentAt,
    }));
  }, [messagesData]);

  const latest = seed && seed.length > 0 ? seed[seed.length - 1] : undefined;
  // Derive the cursor from the initial query, never from the live list,
  // so the subscription isn't torn down and recreated on every message
  // (which would open a gap where rapid follow-ups get missed).
  const subscriptionCursor = seed === undefined ? undefined : (latest?.id ?? null);

  useEffect(() => {
    if (seed === undefined) return;
    setMessages(conversationId, seed);
  }, [seed, conversationId, setMessages]);

  useEffect(() => {
    if (!latest) return;
    setLastMessage(conversationId, latest);
  }, [conversationId, latest, setLastMessage]);

  useEffect(() => {
    const count = unreadData?.unreadChatMessageCount;
    if (typeof count === "number") setUnreadCount(conversationId, count);
  }, [conversationId, setUnreadCount, unreadData]);

  useEffect(() => {
    for (const p of presenceData?.conversationPresence ?? []) {
      setPresence(conversationId, p.personId, p.online, p.observedAt);
    }
  }, [presenceData, conversationId, setPresence]);

  useEffect(() => {
    for (const r of receiptsData?.conversationReadReceipts ?? []) {
      setReadReceipt(conversationId, r.personId, r.lastReadMessageId, r.readAt);
    }
  }, [receiptsData, conversationId, setReadReceipt]);

  const { data: eventData, error: eventError } = useSubscription(ConversationEventsDocument, {
    variables: {
      conversationId,
      afterMessageId: subscriptionCursor ?? undefined,
    },
    skip: subscriptionCursor === undefined,
  });

  useEffect(() => {
    if (eventError) {
      logApplicationFailure("chat.conversation-subscription", {
        code: CODES.application.SUBSCRIPTION_FAILURE,
        level: "warn",
      });
    }
    setLiveError(conversationId, Boolean(eventError));
  }, [eventError, conversationId, setLiveError]);

  useEffect(() => {
    const event = eventData?.conversationEvents;
    if (!event) return;
    if (event.type === "MESSAGE_CREATED" && event.message) {
      const msg: ChatMessage = {
        id: event.message.id,
        conversationId: event.message.conversationId,
        senderId: event.message.senderId,
        content: event.message.content,
        sentAt: event.message.sentAt,
      };
      addMessage(msg);
    } else if (event.type === "TYPING_CHANGED" && event.typing) {
      setTyping(conversationId, event.typing.personId, event.typing.typing ?? false);
    } else if (event.type === "PRESENCE_CHANGED" && event.presence) {
      setPresence(
        conversationId,
        event.presence.personId,
        event.presence.online,
        event.presence.observedAt,
      );
    } else if (event.type === "READ_RECEIPT_UPDATED" && event.readReceipt) {
      setReadReceipt(
        conversationId,
        event.readReceipt.personId,
        event.readReceipt.lastReadMessageId,
        event.readReceipt.readAt,
      );
    }
  }, [addMessage, conversationId, eventData, setTyping, setPresence, setReadReceipt]);

  return null;
}

// Bottom messaging dock. Always mounted so its subscriptions stay live:
//   - MyConversations (refetch on load) seeds the conversation list
//   - ConversationLiveBridge per conversation keeps previews + unread state
//     updated even while a conversation window is closed/minimized
//   - UnreadCountUpdates(conversationId: null) pushes unread counts across
//     all conversations
// feeding the zustand store so the banner list + open windows re-render.
//
// The WS transport is the Fastify BFF proxy that injects the bearer
// server-side (see apps/bff/src/routes/graphql-ws.ts).
export function ChatDock() {
  const setConversations = useChatStore((s) => s.setConversations);
  const addConversation = useChatStore((s) => s.addConversation);
  const removeConversation = useChatStore((s) => s.removeConversation);
  const setMyId = useChatStore((s) => s.setMyId);
  const setUnreadCount = useChatStore((s) => s.setUnreadCount);
  const setCapacity = useChatStore((s) => s.setCapacity);
  const openChats = useChatStore((s) => s.openChats);
  const minimized = useChatStore((s) => s.minimized);
  const conversations = useChatStore((s) => s.conversations);
  const conversationIds = useMemo(() => [...conversations.keys()], [conversations]);

  const { data: convosData } = useQuery(MyConversationsDocument);
  const { data: myselfData } = useQuery(MyselfDocument);

  useEffect(() => {
    setMyId(myselfData?.myself?.id ?? null);
  }, [myselfData, setMyId]);

  const { data: unreadUpdate, error: unreadErr } = useSubscription(
    UnreadCountUpdatesDocument,
    { variables: { conversationId: null } },
  );

  useEffect(() => {
    if (unreadErr) {
      logApplicationFailure("chat.unread-subscription", {
        code: CODES.application.SUBSCRIPTION_FAILURE,
        level: "warn",
      });
    }
  }, [unreadErr]);

  useEffect(() => {
    const u = unreadUpdate?.unreadChatMessageCountUpdates;
    if (!u?.conversationId) return;
    setUnreadCount(u.conversationId, u.count);
  }, [unreadUpdate, setUnreadCount]);

  // Conversations created/deleted while the dock is mounted. Without
  // this, a conversation started by someone else only appears after a
  // reload (MyConversations runs once, and unread updates for unknown
  // IDs are dropped by the store).
  const { data: lifecycle, error: lifecycleErr } = useSubscription(
    ConversationLifecycleEventsDocument,
  );

  useEffect(() => {
    if (lifecycleErr) {
      logApplicationFailure("chat.lifecycle-subscription", {
        code: CODES.application.SUBSCRIPTION_FAILURE,
        level: "warn",
      });
    }
  }, [lifecycleErr]);

  useEffect(() => {
    const e = lifecycle?.conversationLifecycleEvents;
    if (!e) return;
    if (e.type === "CREATED" && e.conversation) {
      const existing = useChatStore.getState().conversations.get(e.conversationId);
      if (!existing) {
        addConversation({
          id: e.conversation.id,
          participantIds: e.conversation.participantIds ?? [],
          unreadCount: 0,
        });
      }
    } else if (e.type === "DELETED") {
      removeConversation(e.conversationId);
    }
  }, [lifecycle, addConversation, removeConversation]);

  useEffect(() => {
    if (!convosData?.conversations) return;
    // setConversations merges (preserves live unread/preview, never drops
    // lifecycle-added entries), so we just hand it the server list.
    setConversations(
      convosData.conversations.map((c) => ({
        id: c.id,
        participantIds: c.participantIds ?? [],
        unreadCount: 0,
      })),
    );
  }, [convosData, setConversations]);

  // Track how many windows fit beside the banner; the store auto-minimizes
  // the oldest visible window(s) whenever capacity drops below the count.
  useEffect(() => {
    const update = () => setCapacity(capacityFor(window.innerWidth));
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [setCapacity]);

  const listOpen = useChatStore((s) => s.listOpen);
  const visible = openChats.filter((id) => !minimized.has(id));
  const minimizedIds = openChats.filter((id) => minimized.has(id));

  return (
    <div
      className="pointer-events-none absolute bottom-0 right-0 z-40 flex max-w-full flex-row-reverse items-end gap-3 px-4"
      data-graphql="MyConversations, Myself, ConversationMessages, UnreadCount, ConversationEvents, UnreadCountUpdates, ConversationLifecycleEvents"
    >
      {conversationIds.map((id) => (
        <ConversationLiveBridge key={id} conversationId={id} />
      ))}
      {listOpen && <MessagesBanner />}
      {minimizedIds.length > 0 && (
        <div className="flex items-end gap-2">
          {minimizedIds.map((id) => (
            <MinimizedChat key={id} conversationId={id} />
          ))}
        </div>
      )}
      {visible.map((id) => (
        <ChatWindow key={id} conversationId={id} />
      ))}
    </div>
  );
}
